import {
  Button,
  Checkbox,
  Divider,
  Input,
  InputNumber,
  Select,
  Space,
  Typography,
  message,
} from "antd";
import { useEffect, useState } from "react";
import {
  CancelWalletRefundItem,
  cancelAndWalletRefund,
  getPurchaseOrderDetails,
} from "../../../../apis/po.api";
import { getImageUrl } from "../../../../helpers/image_helper";
import { ApprovalReasons } from "../../../../pages/refunds-v2";
import { usePermissions } from "../../../../custom_hooks/permission/permission_hook";

export interface CancelWalletRefundDrawerProps {
  po_id: string | number;
  onSuccess?: () => void;
}

export function CancelWalletRefundDrawer({
  po_id,
  onSuccess,
}: CancelWalletRefundDrawerProps) {
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission("po.update");
  const [poItems, setPoItems] = useState<any[]>([]);
  const [itemQuantityMap, setItemQuantityMap] = useState<Record<string, number>>({});
  const [reason, setReason] = useState<string | null>(null);
  const [customRemark, setCustomRemark] = useState<string>("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!po_id) return;
    getPurchaseOrderDetails(String(po_id)).then((details) => {
      setPoItems(details?.po_items || []);
    });
  }, [po_id]);

  const handleSubmit = async () => {
    if (!canEdit) {
      message.error("You don't have permission to perform this action");
      return;
    }
    const selectedItems: CancelWalletRefundItem[] = Object.entries(itemQuantityMap)
      .filter(([, qty]) => qty > 0)
      .map(([id, qty]) => ({ po_item_id: Number(id), quantity: qty }));

    if (selectedItems.length === 0) {
      message.error("Select at least one item with quantity > 0");
      return;
    }
    if (!reason) {
      message.error("Please select a reason");
      return;
    }
    if (reason === "Others" && !customRemark.trim()) {
      message.error("Please enter a remark");
      return;
    }

    setLoading(true);
    const result = await cancelAndWalletRefund({
      po_items: selectedItems,
      reason: reason === "Others" ? customRemark : reason,
    });
    setLoading(false);

    if (result) {
      onSuccess?.();
    }
  };

  return (
    <div>
      <Typography.Text type="secondary" style={{ display: "block", marginBottom: "1rem" }}>
        Select items and quantities to cancel. The refund amount will be credited to the
        customer's wallet immediately.
      </Typography.Text>

      {poItems.map((item: any) => {
        const itemId = String(item.id);
        const maxQty = Number(item.quantity ?? 0);
        return (
          <Space
            key={itemId}
            style={{ width: "100%", marginBottom: "0.75rem", justifyContent: "space-between" }}
          >
            <Checkbox
              checked={(itemQuantityMap[itemId] ?? 0) > 0}
              onChange={(e) => {
                setItemQuantityMap((prev) => ({
                  ...prev,
                  [itemId]: e.target.checked ? 1 : 0,
                }));
              }}
            >
              <Space>
                <img
                  src={getImageUrl(item?.estimate_item?.item_image, "50")}
                  alt=""
                  width={50}
                  height={50}
                  style={{ borderRadius: 4, objectFit: "contain", background: "#f0f0f0" }}
                />
                <div>
                  <div>{item?.estimate_item?.boq_item}</div>
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    ₹{item?.unit_po_price} × {maxQty} units
                  </div>
                </div>
              </Space>
            </Checkbox>
            <InputNumber
              min={0}
              max={maxQty}
              value={itemQuantityMap[itemId] ?? 0}
              onChange={(value) => {
                setItemQuantityMap((prev) => ({
                  ...prev,
                  [itemId]: value ?? 0,
                }));
              }}
              style={{ width: 90 }}
            />
          </Space>
        );
      })}

      <Divider />

      <div className="mb-3">
        <Typography.Text strong>Reason</Typography.Text>
        <Select
          className="w-100 mt-1"
          placeholder="Select reason"
          value={reason}
          onChange={setReason}
        >
          {ApprovalReasons.map((r) => (
            <Select.Option key={r} value={r}>
              {r}
            </Select.Option>
          ))}
        </Select>
        {reason === "Others" && (
          <Input.TextArea
            className="mt-2"
            placeholder="Enter remark"
            value={customRemark}
            onChange={(e) => setCustomRemark(e.target.value)}
            rows={3}
          />
        )}
      </div>

      <Button
        type="primary"
        danger
        loading={loading}
        onClick={handleSubmit}
        disabled={Object.values(itemQuantityMap).every((q) => q === 0) || !canEdit}
        style={{ marginTop: "0.5rem" }}
      >
        Cancel & Refund to Wallet
      </Button>
    </div>
  );
}
